/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useDialRuleRestrictionForm.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Hook for managing Dial Rule Restriction form state and submission.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { createDialRuleRestriction, updateDialRuleRestriction } from '../api/dialRuleRestrictionsApi';
import { FormState, RuleFormState, ValidationErrors } from '../types';
import { validateDialRuleRestrictionForm } from '../utils/validators';
import { getDefaultFormState, cloneFormState, createEmptyRule } from '../utils/formDefaults';
import { applySequentialPriorities, serializeFormState } from '../utils/ruleHelpers';

/**
 * Hook for Dial Rule Restriction form management
 */
export function useDialRuleRestrictionForm(
  initialFormData: FormState | null,
  id: string | undefined,
  isEditMode: boolean
) {
  const { showSuccess, showError, showInfo } = useNotifications();
  const { t } = useI18n();
  const showErrorRef = useRef(showError);
  const showInfoRef = useRef(showInfo);

  const defaultFormState = useMemo(() => getDefaultFormState(), []);
  const [formData, setFormData] = useState<FormState>(
    initialFormData || defaultFormState
  );
  const [originalData, setOriginalData] = useState<FormState>(
    initialFormData || defaultFormState
  );
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    showErrorRef.current = showError;
    showInfoRef.current = showInfo;
  }, [showError, showInfo]);

  // Sync with initial data when it changes
  useEffect(() => {
    if (initialFormData) {
      const currentSerialized = JSON.stringify(initialFormData);
      const lastSerialized = JSON.stringify(originalData);

      if (currentSerialized !== lastSerialized) {
        setFormData(initialFormData);
        setOriginalData(cloneFormState(initialFormData));
        setErrors({});
      }
    } else if (!isEditMode && !initialFormData) {
      const defaultState = getDefaultFormState();
      setFormData(defaultState);
      setOriginalData(cloneFormState(defaultState));
      setErrors({});
    }
  }, [initialFormData, isEditMode, originalData]);

  const hasChanges = useMemo(() => {
    if (!isEditMode) {
      return true;
    }

    const current = JSON.stringify(serializeFormState(formData));
    const baseline = JSON.stringify(serializeFormState(originalData));
    return current !== baseline;
  }, [formData, originalData, isEditMode]);

  const handleBaseChange = useCallback(
    <K extends keyof FormState>(field: K, value: FormState[K]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field as string]) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[field as string];
          return next;
        });
      }
    },
    [errors]
  );

  const handleAddRule = useCallback(() => {
    setFormData((prev) => {
      const nextRules = applySequentialPriorities([
        ...prev.rules,
        createEmptyRule(prev.rules.length * 10),
      ]);
      return { ...prev, rules: nextRules };
    });
  }, []);

  const handleRemoveRule = useCallback((index: number) => {
    setFormData((prev) => {
      const nextRules = prev.rules.filter((_, ruleIndex) => ruleIndex !== index);
      return { ...prev, rules: applySequentialPriorities(nextRules) };
    });
  }, []);

  const handleUpdateRule = useCallback(
    (index: number, field: keyof RuleFormState, value: unknown) => {
      setFormData((prev) => {
        const nextRules = prev.rules.map((rule, ruleIndex) =>
          ruleIndex === index ? { ...rule, [field]: value } : rule
        );
        return { ...prev, rules: applySequentialPriorities(nextRules) };
      });
    },
    []
  );

  const handleDragEnd = useCallback(
    (activeId: string, overId: string | null) => {
      if (!overId || activeId === overId) {
        return;
      }

      setFormData((prev) => {
        const oldIndex = prev.rules.findIndex((rule) => rule.clientId === activeId);
        const newIndex = prev.rules.findIndex((rule) => rule.clientId === overId);
        if (oldIndex === -1 || newIndex === -1) {
          return prev;
        }

        const nextRules = [...prev.rules];
        const [moved] = nextRules.splice(oldIndex, 1);
        nextRules.splice(newIndex, 0, moved);
        return { ...prev, rules: applySequentialPriorities(nextRules) };
      });
    },
    []
  );

  const validateForm = useCallback(() => {
    const validationErrors = validateDialRuleRestrictionForm(formData, t);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      if (validationErrors.name) {
        showErrorRef.current(validationErrors.name);
      } else if (validationErrors.rules) {
        showErrorRef.current(validationErrors.rules);
      }
      return false;
    }

    return true;
  }, [formData, t]);

  // Handle submit - Always-Active Save Standard: always attempt to save
  const handleSubmit = useCallback(async (): Promise<FormState & { id?: number; uuid?: string } | undefined> => {
    if (!validateForm()) {
      return undefined;
    }

    setIsSaving(true);
    try {
      const payload = serializeFormState(formData);
      const result = isEditMode
        ? await updateDialRuleRestriction(id!, payload, t)
        : await createDialRuleRestriction(payload, t);

      showSuccess(
        isEditMode
          ? t('dialRuleRestrictions.messages.updateSuccess')
          : t('dialRuleRestrictions.messages.createSuccess')
      );

      setOriginalData(cloneFormState(formData));

      // Return data with ID for navigation
      return { ...formData, id: result.data?.id, uuid: result.data?.uuid } as any;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : isEditMode
            ? t('dialRuleRestrictions.messages.updateError')
            : t('dialRuleRestrictions.messages.createError');
      showErrorRef.current(message);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [formData, hasChanges, id, isEditMode, showSuccess, t, validateForm]);

  const handleCancel = useCallback(() => {
    if (!isEditMode) {
      const reset = getDefaultFormState();
      setFormData(reset);
      setErrors({});
      return;
    }

    setFormData(cloneFormState(originalData));
    setErrors({});
  }, [isEditMode, originalData]);

  return {
    formData,
    setFormData,
    originalData,
    errors,
    isSaving,
    hasChanges,
    handleBaseChange,
    handleAddRule,
    handleRemoveRule,
    handleUpdateRule,
    handleDragEnd,
    handleSubmit,
    handleCancel,
    validateForm,
  };
}

